<template>
  <g
    @mousedown="configuration ? (active = true) : null"
    @mousemove="active ? move($event) : null"
    @mouseup="active = false"
  >
    <mce-line
      :class="['pf-annotation-line', lineStyles]"
      :stroke="color"
      :stroke-width="width"
      :stroke-dasharray="`${dashWidth} ${dashSpace}`"
      :x1="lineX1"
      :x2="lineX2"
      :y1="lineY1"
      :y2="lineY2"
    />
    <rect
      v-if="selected && configuration"
      :class="['pf-annotation-line__anchor']"
      stroke="#0077CF"
      :stroke-width="2"
      fill="white"
      :x="rectX1"
      :y="rectY1"
      :width="10"
      :height="10"
    />
    <rect
      v-if="selected && configuration"
      :class="['pf-annotation-line__anchor']"
      stroke="#0077CF"
      :stroke-width="2"
      fill="white"
      :x="rectX2"
      :y="rectY2"
      :width="10"
      :height="10"
    />
    <circle
      v-if="configuration && selected"
      class="pf-annotation-line--mover"
      :cx="moverX"
      :cy="moverY"
      :r="12"
      fill="white"
      :stroke-width="1"
    />
    <g
      v-if="configuration && selected"
      class="pf-annotation-line--mover-icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      :transform="`translate(${iconX} ${iconY})`"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M14.22 6.573a.75.75 0 1 0 1.06-1.06l-2.145-2.146a1.25 1.25 0 0 0-1.77 0L9.22 5.513a.75.75 0 0 0 1.06 1.06l1.22-1.22v5.899H5.602l1.22-1.22a.75.75 0 0 0-1.06-1.06l-2.147 2.145a1.251 1.251 0 0 0 0 1.77l2.146 2.145a.75.75 0 1 0 1.06-1.06l-1.219-1.22H11.5v5.897l-1.22-1.22a.75.75 0 1 0-1.06 1.061l2.145 2.146a1.248 1.248 0 0 0 1.77 0l2.145-2.146a.75.75 0 1 0-1.06-1.06L13 18.65v-5.898h5.898l-1.22 1.22a.75.75 0 0 0 1.06 1.06l2.147-2.146a1.252 1.252 0 0 0 0-1.77l-2.146-2.145a.75.75 0 0 0-1.06 1.06l1.219 1.22H13V5.354l1.22 1.22Z"
        fill="currentColor"
      ></path>
    </g>
    <pf-position-tag
      v-if="selected && configuration"
      :tag-x="tagX"
      :tag-y="tagY"
      :text-x="numberX"
      :text-y="numberY"
      :text="tagText"
      :width="tagWidth"
      :height="tagHeight"
    ></pf-position-tag>
  </g>
</template>

<script>
  import formatValue from '@/utils/valueFormatter';
  import countDecimals from '@/utils/countDecimals';
  import MceLine from '@mce/line';
  import PfPositionTag from './PositionTag.vue';
  import repoMixin from '../../mixins/interactions/reposition';

  export default {
    name: 'PfLine',
    components: {
      MceLine,
      PfPositionTag,
    },
    mixins: [repoMixin],
    props: {
      id: {
        type: String,
      },
      bandX: {
        type: Array,
      },
      color: {
        type: String,
        default: '#5e5e5e',
      },
      configuration: {
        type: Boolean,
      },
      data: {
        type: Array,
        required: true,
      },
      domainX: {
        type: Array,
        required: true,
      },
      domainY: {
        type: Array,
        required: true,
      },
      horizontal: {
        type: Boolean,
        default: false,
      },
      start: {
        type: Object,
      },
      plot: {
        type: Object,
      },
      rangeX: {
        type: Array,
        required: true,
      },
      rangeY: {
        type: Array,
        required: true,
      },
      scaleTypeX: {
        type: String,
        default: 'linear',
      },
      scaleTypeY: {
        type: String,
        default: 'linear',
      },
      selected: {
        type: Boolean,
      },
      transpose: {
        type: Boolean,
        default: false,
      },
      width: {
        type: Number,
        default: 2,
      },
      dashWidth: {
        type: Number,
        default: 1,
      },
      dashSpace: {
        type: Number,
        default: 0,
      },
      setX: {
        type: [Number, String, Date, Object],
      },
      setY: {
        type: Number,
      },
      valueFormat: {
        type: Object,
      },
    },
    computed: {
      lineStyles() {
        const obj = {};
        obj['pf-annotation-line--config'] = this.configuration;
        obj['pf-annotation-line--selected'] = this.selected && this.configuration;

        return obj;
      },
      lineX1() {
        const x =
          this.configuration && this.dynamicStart != null && this.dynamicStart.x != null
            ? this.dynamicStart.x
            : this.plotX;
        return this.horizontal ? this.rangeX[0] : x;
      },
      lineX2() {
        const x =
          this.configuration && this.dynamicStart != null && this.dynamicStart.x != null
            ? this.dynamicStart.x
            : this.plotX;
        return this.horizontal ? this.rangeX[1] : x;
      },
      lineY1() {
        const y =
          this.configuration && this.dynamicStart != null && this.dynamicStart.y != null
            ? this.dynamicStart.y
            : this.plotY;
        return this.horizontal ? y : this.rangeY[0];
      },
      lineY2() {
        const y =
          this.configuration && this.dynamicStart != null && this.dynamicStart.y != null
            ? this.dynamicStart.y
            : this.plotY;
        return this.horizontal ? y : 0;
      },
      plotX() {
        return this.plot != null ? this.getScaleX(this.plot.x) : this.start.x;
      },
      plotY() {
        return this.plot != null ? this.yScale(this.plot.y) : this.start.y;
      },
      rectX1() {
        const add = this.horizontal ? 0 : -5;
        return this.lineX1 + add;
      },
      rectX2() {
        const add = this.horizontal ? -10 : -5;
        return this.lineX2 + add;
      },
      rectY1() {
        const add = this.horizontal ? -5 : -10;
        return this.lineY1 + add;
      },
      rectY2() {
        const add = this.horizontal ? -5 : 0;
        return this.lineY2 + add;
      },
      tagOffset() {
        return !this.horizontal && this.scaleTypeX === 'time' ? 30 : 10;
      },
      tagText() {
        let value = this.horizontal
          ? this.setYPosition(this.lineY1)
          : this.setXPosition(this.lineX2);

        if (typeof value !== 'number') {
          const d = new Date(value);
          return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
        }

        value = countDecimals(value) > 2 ? value.toFixed(2) : value;

        if (
          (this.horizontal && this.scaleTypeY === 'linear') ||
          (!this.horizontal && this.scaleTypeX === 'linear')
        ) {
          return formatValue(value, this.valueFormat);
        }
        return value;
      },
      tagX() {
        const start = this.horizontal ? this.lineX1 : this.lineX2;
        const offset = this.horizontal ? this.tagWidth : this.tagWidth / 2;

        return start - offset;
      },
      tagY() {
        const offset = this.horizontal ? -10 : 2;
        return this.lineY1 + offset;
      },
      numberX() {
        const start = this.horizontal ? this.lineX1 : this.lineX2;
        const offset = this.horizontal ? this.tagWidth / 2 + this.tagOffset : this.tagOffset;
        return start - offset;
      },
      numberY() {
        const offset = this.horizontal ? 6 : 20;
        return this.lineY1 + offset;
      },
      moverX() {
        return this.horizontal
          ? (this.lineX1 + this.lineX2) / 2
          : this.lineX1 + this.moveRadius + this.movePadding;
      },
      moverY() {
        return this.horizontal
          ? this.lineY1 + this.moveRadius + this.movePadding
          : (this.lineY1 + this.lineY2) / 2;
      },
      iconX() {
        return this.horizontal
          ? (this.lineX1 + this.lineX2) / 2 - this.moveRadius
          : this.lineX1 + this.moveRadius + 2;
      },
      iconY() {
        return this.horizontal
          ? this.lineY1 + this.movePadding
          : (this.lineY1 + this.lineY2) / 2 - this.moveRadius;
      },
    },
    data() {
      return {
        active: false,
        init: false,
        padding: 6,
        tagWidth: 90,
        tagHeight: 22,
        moveRadius: 12,
        movePadding: 14,
      };
    },
    methods: {
      move(ev) {
        this.configProp = 'plot';
        this.repositioning(ev);
      },
    },
    watch: {
      plot() {
        if (this.init) {
          this.dynamicStart = this.start;
          this.init = false;
        }
      },
      setX(val) {
        let newX = this.scaleTypeX === 'time' ? this.getScaleX(new Date(val)) : this.getScaleX(val);
        this.dynamicStart = this.dynamicStart ?? {};
        newX = typeof val === 'object' ? this.dynamicStart.x : newX;
        this.$set(this.dynamicStart, 'x', newX);
      },
      setY(val) {
        this.dynamicStart = this.dynamicStart ?? {};
        this.$set(this.dynamicStart, 'y', this.yScale(val));
      },
      transpose(val) {
        this.transposeScale = val;
      },
    },
    mounted() {
      this.configProp = 'plot';
      this.transposeScale = this.transpose;
      if (this.configuration && this.plot == null) {
        this.init = true;
        this.updatePosition('plot', this.start);
      }
    },
  };
</script>

<style lang="scss">
  @import '@mce/visual-language';
  @import '@mds/constants';

  .pf-annotation-line {
    shape-rendering: auto;
    opacity: 1;

    &--config:hover {
      stroke: $mds-interactive-color-primary-default;
      cursor: grab;
    }

    &--selected {
      fill: $mds-interactive-color-primary-default;
    }

    &__anchor,
    &__tag {
      cursor: grab;
    }

    &--mover {
      stroke: $mds-interactive-color-primary-default;
      fill: white;

      &:hover {
        fill: $mds-interactive-color-primary-default;
        cursor: grab;
      }

      &--mover-icon:hover {
        cursor: grab;

        .pf-annotation-line--mover & {
          fill: $mds-interactive-color-primary-default;
        }
      }
    }
  }
</style>
